import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import auth from '../utils/auth';
import Sidebar from '../components/dash/Sidebar';
import Navbar from '../components/dash/Navbar';
import ReportViewer from '../components/dash/ReportViewer';
import { getUserProfile } from '../utils/api_dashboard';
import FloatingNavToggle from '../components/dash/FloatingNavToggle';
import { setCookie } from '../utils/helpers';

const DashboardPage = () => {
    const history = useHistory();
    const location = useLocation();
    const [user, setUser] = useState(auth.getCurrentUser() || {});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportTiles, setReportTiles] = useState(null);
    const [anomalyCount, setAnomalyCount] = useState({});
    const [customLogo, setCustomLogo] = useState({ hasCustomLogo: false, logo_path: '', logo_thumb_path: '' });
    const [navbarVisible, setNavbarVisible] = useState(() => 
        localStorage.getItem('navbarVisible') !== 'false' // Default to visible
    );
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => 
        localStorage.getItem('sidebarCollapsed') === 'true'
    );

    const toggleNavbar = () => {
        setNavbarVisible(prev => {
            const newState = !prev;
            localStorage.setItem('navbarVisible', newState);
            return newState;
        });
    };

    // Toggle function to be passed to both components
    const toggleSidebar = () => {
        setSidebarCollapsed(prev => !prev);
    };

    // Add effect to update page title when selectedReport changes
    useEffect(() => {
        if (selectedReport) {
            // Use the report's friendly name or fallback to report name
            const reportTitle = selectedReport.reportFriendly || selectedReport.reportName;
            document.title = `Parki | ${reportTitle}`;
        } else {
            document.title = 'Parki | Dashboard';
        }
    }, [selectedReport]);

    // Handle report selection
    const handleReportSelect = (report) => {

        // set the selected report 
        setSelectedReport(report);
        
        // Store selected report in local storage to persist across page refreshes
        localStorage.setItem('selectedReport', JSON.stringify(report));
    };

    // Fetch user profile data when component mounts
    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                // Check if user is authenticated
                if (!auth.isAuthenticated()) {
                    history.push('/login');
                    return;
                }
                
                // Fetch user profile
                const profileData = await getUserProfile();

                // build a tmp user data variable
                const tmpUser = {
                    firstName: profileData?.user?.given_name,
                    lastName: profileData?.user?.family_name,
                    userType: profileData?.userData?.userType || 'customer',
                    entityName: profileData?.userData?.entityName || 'unknown',
                    entityId: parseInt(profileData?.userData?.linkedEntity) || null,
                    isEntityAdmin: profileData?.userData?.isEntityAdmin || false,
                    userId: profileData?.userData?.userId || null,
                    email: profileData?.user?.email || '',
                    defaultTV: profileData?.userData?.DefaultTV || -1
                };

                // update user data in local storage
                auth.setCurrentUser(tmpUser);

                // Update user state with profile data
                setUser(tmpUser);

                // set the anomaly count
                setAnomalyCount(profileData?.anomaly_count || {});

                // set custom logo info
                setCustomLogo({
                    hasCustomLogo: profileData?.custom_logo?.hasCustomLogo || false,
                    logo_path: profileData?.custom_logo?.logo_path || '',
                    logo_thumb_path: profileData?.custom_logo?.logo_thumb_path || ''
                });

                // get the Parki API Token
                const parkApiToken = profileData?.parkiAPIToken || null;
                if (parkApiToken) {
                    const cookieDomain = window.location.hostname.endsWith('parki.cloud')
                        ? '.parki.cloud'
                        : window.location.hostname; // dev/local fallback
                    // Prefer SameSite=Lax for same-site subdomains; use None only if needed
                    setCookie('parki_api_token', parkApiToken, {
                        days: 1,
                        domain: cookieDomain,
                        path: '/',
                        sameSite: 'None',
                        secure: true
                    });

                    // Cookie is now set on .parki.cloud — safe to send the user back
                    // to the originating site (e.g. anomalies.parki.cloud) if they
                    // arrived via parki_auth_redirect_to. Skip the rest of the
                    // dashboard setup since we're navigating away.
                    if (auth.consumePendingAuthRedirect()) {
                        return;
                    }
                }

                // Check URL parameters for report selection
                const queryParams = new URLSearchParams(location.search);
                const reportParam = queryParams.get('report');

                ////////////////////////////////////////////////////////////
                // Booking Trend Report Specified from Anomaly Detection //
                //////////////////////////////////////////////////////////

                // If "booking-trend" is specified in the URL, load that report
                if (reportParam === 'booking-trend') {
                    
                    // Get the booking trend report, otherwise throw an error 
                    const bookingTrendReport = profileData?.reports?.find(report => report.reportId === 58);
                    if (!bookingTrendReport) {
                        throw new Error('Booking Trend report (ID 58) not found in user reports');
                    }

                    // Set reports state
                    setReports(profileData.reports);
                    
                    setSelectedReport(bookingTrendReport);
                    localStorage.setItem('selectedReport', JSON.stringify(bookingTrendReport));

                // otherwise, if no report param, proceed with normal logic
                } else if (profileData?.reports && profileData.reports.length > 0) {
                    
                    // Set reports state
                    setReports(profileData.reports);


                    ////////////////////////////////////////////////////////////
                    // Load the previously selected report from localStorage //
                    //////////////////////////////////////////////////////////
                    
                    // Load previously selected report from localStorage or select the first one
                    const savedReport = localStorage.getItem('selectedReport');
                    if (savedReport) {
                        setSelectedReport(JSON.parse(savedReport));
                    } else if (profileData.reports.length > 0) {

                        // Extract tile reports first to check for report67
                        const tileReports = profileData.reports.filter(
                            report => report.locationType === 'tile' && report.uriPath
                        );
                        
                        // Set reportTiles state for potential use elsewhere
                        setReportTiles(tileReports);
                        
                        // Look for report67 in tile reports first
                        const tileReport67 = tileReports.find(report => report.reportId === 67);
                        
                        ////////////////////////
                        // Dashboard Reports //
                        //////////////////////

                        const dashboardReports = profileData.reports.filter(
                            report => report.locationType === 'dashboard' && report.uriPath
                        );
                        
                        if (dashboardReports.length > 0) {
                            
                            // First check if report67 exists in tile reports -- Anomaly Detection Load as 1st choice --
                            if (tileReport67) {
                                setSelectedReport(tileReport67);
                                localStorage.setItem('selectedReport', JSON.stringify(tileReport67));
                            } else {
                                // Fall back to the original logic if report67 isn't in tile reports
                                const report67 = dashboardReports.find(report => report.reportId === 67);
                                const report6 = dashboardReports.find(report => report.reportId === 6);
                                setSelectedReport(report67 || report6 || dashboardReports[0]);
                                localStorage.setItem('selectedReport', JSON.stringify(report67 || report6 || dashboardReports[0]));
                            }
                        } else if (tileReport67) {
                            // If no dashboard reports but we found report67 in tiles
                            setSelectedReport(tileReport67);
                            localStorage.setItem('selectedReport', JSON.stringify(tileReport67));
                        }
                    }
                }
                setLoading(false);
                
            } catch (error) {
                console.error('Error fetching user profile:', error);
                setError('Failed to load user profile');
                setLoading(false);
                
                // If unauthorized (401), redirect to login
                if (error.response && error.response.status === 401) {
                    auth.logout();
                    history.push('/login');
                }
            }
        };
        fetchUserProfile();
    }, [history]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden"></span>
                </div>
            </div>
        );
    }

    return (
        <div id="dashboard" className={`layout-wrapper layout-content-navbar ${!navbarVisible ? 'layout-without-navbar' : ''}`}>
            <div className="layout-container">

                {/* Sidebar component */}
                <Sidebar 
                    activeItem="dashboard" 
                    collapsed={sidebarCollapsed} 
                    toggleSidebar={toggleSidebar}
                    reports={reports}
                    selectedReport={selectedReport}
                    onSelectReport={handleReportSelect}
                    anomalyAlerts={anomalyCount}
                    customLogo={customLogo}
                    user={user}
                />

                {/* Layout page */}
                <div className="layout-page">

                    {/* Navbar component - with selectedReport */}
                    {navbarVisible && (
                        <Navbar 
                            user={user} 
                            toggleSidebar={toggleSidebar} 
                            selectedReport={selectedReport}
                            sidebarCollapsed={sidebarCollapsed}
                        />
                    )}

                    {/* Content */}
                    <div className="content-wrapper">

                        <div className="container-xxl flex-grow-1 container-p-y" style={{ maxWidth: '1700px' }}>
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}
                            
                            {/* Report Viewer */}
                            {selectedReport ? (
                                <ReportViewer 
                                    report={selectedReport} 
                                    navbarVisible={navbarVisible} 
                                />
                            ) : (
                                <div className="text-center my-5">
                                    <h3>Welcome to your dashboard</h3>
                                    <p>Please select a report from the sidebar to view</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Overlay */}
            <div className="layout-overlay layout-menu-toggle"></div>
            
            {/* Drag Target for Mobile */}
            <div className="drag-target"></div>

            <FloatingNavToggle visible={navbarVisible} onToggle={toggleNavbar} offset={0} />
        </div>
    );
};

export default DashboardPage;